import React, { type FC } from 'react';

import { useField } from 'formik';

import { Box } from '@clutter/clean';

import { Button, Table } from '@shared/components/bootstrap';
import { useToggle } from '@shared/hooks/use_toggle';

import {
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
  useConfirmation,
} from '@admin/components/confirmation';
import { Panel } from '@admin/components/helpers/panel';
import type { Landing__Location__FactInput } from '@admin/schema';

import { LandingLocationFactModal } from './modal';

const LandingLocationFactDeleteButton: FC<{
  fact: Landing__Location__FactInput;
  onDelete(): void;
}> = ({ fact, onDelete }) => {
  const { confirmation, confirm } = useConfirmation();
  const onClick = async () => {
    const confirmed = await confirm({
      title: <>Are you sure you want to delete {fact.title}?</>,
      description: <>Deleting is permanent.</>,
    });
    if (confirmed !== ConfirmationResponse.Confirm) return;
    onDelete();
  };

  return (
    <>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}
      <Button kind="danger" onClick={onClick}>
        Delete
      </Button>
    </>
  );
};

const LandingLocationFactEditButton: FC<{
  fact: Landing__Location__FactInput;
  onSave(_: Landing__Location__FactInput): void;
}> = ({ fact, onSave }) => {
  const { visible, onToggle } = useToggle();
  return (
    <>
      <Button kind="primary" onClick={() => onToggle()}>
        Edit
      </Button>
      {visible && (
        <LandingLocationFactModal
          onSave={(input) => {
            onSave(input);
            onToggle();
          }}
          onClose={() => onToggle()}
          fact={fact}
        />
      )}
    </>
  );
};

const LandingLocationFactNewButton: FC<{
  onSave(_: Landing__Location__FactInput): void;
}> = ({ onSave }) => {
  const { visible, onToggle } = useToggle();
  return (
    <>
      <Button kind="primary" onClick={onToggle}>
        Add
      </Button>
      {visible && (
        <LandingLocationFactModal
          onSave={(input) => {
            onSave(input);
            onToggle();
          }}
          onClose={() => onToggle()}
        />
      )}
    </>
  );
};

export const LandingLocationFactsPanel: FC = () => {
  const [field, _, helpers] = useField<Array<Landing__Location__FactInput>>('facts');

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>Facts</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        <Table striped>
          <thead>
            <tr>
              <th className="text-left">Title</th>
              <th className="text-left">Description</th>
              <th className="col-zero" />
            </tr>
          </thead>
          <tbody>
            {field.value.map(
              (fact, index) =>
                !fact._destroy && (
                  <tr key={index}>
                    <td className="text-left">{fact.title}</td>
                    <td className="text-left">{fact.description}</td>
                    <td className="col-zero">
                      <Box.Flex gap="4px">
                        <LandingLocationFactEditButton
                          fact={fact}
                          onSave={(input) => {
                            const facts = [...field.value];
                            facts[index] = {
                              ...facts[index],
                              ...input,
                            };
                            helpers.setValue(facts);
                          }}
                        />
                        <LandingLocationFactDeleteButton
                          fact={fact}
                          onDelete={() => {
                            const facts = [...field.value];
                            facts[index] = {
                              ...facts[index],
                              _destroy: !facts[index]._destroy,
                            };
                            helpers.setValue(facts);
                          }}
                        />
                      </Box.Flex>
                    </td>
                  </tr>
                ),
            )}
          </tbody>
        </Table>
      </Panel.Body>
      <Panel.Footer align="right">
        <LandingLocationFactNewButton
          onSave={(input) => {
            const facts = [...field.value];
            facts.push(input);
            helpers.setValue(facts);
          }}
        />
      </Panel.Footer>
    </Panel>
  );
};
